import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import { CircularProgress } from "@mui/material";
import MapContainer from "../../components/MapContainer";
import OrganizationAreaLayer from "../../components/mapview/OrganizationAreaLayer";
import { useGoogleMap } from "@react-google-maps/api";
import MarkersClusterOverlay from "../../components/MarkersClusterOverlay";
import AdvancedMarker from "../../components/AdvancedMarker";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { HeatmapLayer as DeckHeatmapLayer } from "@deck.gl/aggregation-layers";
import decodePolyline from "decode-google-map-polyline";
import { partialType } from "../../constants/partialType";
import startIcon from "../../images/icons/pin-start.svg";
import finishIcon from "../../images/icons/pin-stop.svg";
import pauseIcon from "../../images/icons/pin-pause.svg";
import resumeIcon from "../../images/icons/pin-resume.svg";
import {
    useGetOrganizationPolylines,
    useGetOrganizationPolylinesAll,
    useGetOrganizationPartials,
    useGetOrganizationPartialsAll,
    useGetOrganization,
    REFETCH_OPTIONS,
} from "../../services/ContentManager";
import makeStyles from '@mui/styles/makeStyles';

const HEATMAP_COLOR_RANGE = Object.freeze([
    [102, 255, 0],
    [147, 255, 0],
    [198, 255, 0],
    [255, 255, 0],
    [255, 198, 0],
    [255, 147, 0],
    [255, 102, 0],
    [255, 0, 0],
]);
const HEATMAP_GRADIENT_CSS = `linear-gradient(to right, ${HEATMAP_COLOR_RANGE
    .map(([r, g, b]) => `rgb(${r}, ${g}, ${b})`)
    .join(', ')})`;

const useStyles = makeStyles(() => ({
    root: { width: "100%", height: "70vh", position: "relative" },
    legendStack: {
        position: "absolute",
        bottom: 10,
        left: 10,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        zIndex: 1000,
    },
    legend: {
        padding: 10,
        backgroundColor: "#fff",
        borderRadius: 5,
        boxShadow: "0 0 10px rgba(0,0,0,0.2)",
    },
    colorBar: {
        width: 150,
        height: 20,
        borderRadius: 3,
        background: HEATMAP_GRADIENT_CSS,
        margin: "4px 0",
    },
    legendTitle: {
        fontWeight: "bold",
        marginBottom: 6,
        fontSize: 13,
    },
    clusterColorBar: {
        width: 150,
        height: 20,
        borderRadius: 3,
        background: CLUSTER_GRADIENT_CSS,
        margin: "4px 0",
    },
    clusterScaleLabels: {
        display: "flex",
        justifyContent: "space-between",
        fontSize: 12,
    },
}));

// Scala cromatica continua dei cluster dei parziali (numerosità sovrapposte).
// Sfumatura blu → viola → rosso, volutamente diversa da quella della heatmap
// (verde→giallo→rosso). Il colore è interpolato in funzione del conteggio,
// normalizzato sul massimo dei cluster attualmente mostrati dalla mappa:
// il cluster più numeroso visibile è rosso, gli altri scalano di conseguenza.
const CLUSTER_GRADIENT = ['#2563eb', '#9333ea', '#dc2626']; // blu → viola → rosso
const CLUSTER_GRADIENT_CSS = `linear-gradient(to right, ${CLUSTER_GRADIENT.join(', ')})`;
const CLUSTER_FILL_OPACITY = 0.85;
const CLUSTER_STYLE_CACHE_LIMIT = 512;

function hexToRgb(hex) {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h.substring(0, 6);
    const num = parseInt(full, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('');
}

function gradientMax(maxCount) {
    const safeMax = Math.max(1, maxCount || 0);
    return Math.max(1, Math.floor(safeMax * 0.9));
}

function heatmapWeight(weight, scaleMax) {
    return Math.min(Math.max(0, weight || 0), scaleMax);
}

// Limita il gradiente al 90° percentile dei valori visibili. Rispetto al
// massimo assoluto, evita che pochi picchi isolati rendano quasi tutta la
// heatmap verde e rende confrontabili le zone più dense nel viewport corrente.
function heatmapScaleMax(points) {
    if (!points.length) return 1;
    const weights = points
        .map(point => Math.max(1, point.weight || 0))
        .sort((a, b) => a - b);
    const percentileIndex = Math.ceil(weights.length * 0.9) - 1;
    return weights[Math.max(0, percentileIndex)];
}

// Posizione 0..1 del conteggio sulla scala (logaritmica, così i conteggi
// bassi si distribuiscono meglio). Il gradiente raggiunge il colore finale
// al 90% del massimo mostrato.
function clusterNorm(count, maxCount) {
    const maxGradientValue = gradientMax(maxCount);
    const denom = Math.log(Math.max(2, maxGradientValue));
    return Math.min(1, Math.log(Math.max(1, count)) / denom);
}

// Colore interpolato lungo CLUSTER_GRADIENT in base al conteggio.
function clusterColor(count, maxCount) {
    const norm = clusterNorm(count, maxCount);
    const segments = CLUSTER_GRADIENT.length - 1;
    const scaled = norm * segments;
    const idx = Math.min(segments - 1, Math.floor(scaled));
    const t = scaled - idx;
    const a = hexToRgb(CLUSTER_GRADIENT[idx]);
    const b = hexToRgb(CLUSTER_GRADIENT[idx + 1]);
    return rgbToHex(a.r + (b.r - a.r) * t, a.g + (b.g - a.g) * t, a.b + (b.b - a.b) * t);
}

// Restituisce un colore testo (chiaro o scuro) che contrasta col pallino,
// tenendo conto dell'opacità del riempimento (fuso su sfondo mappa chiaro).
function getContrastText(hex, opacity = 1) {
    const { r, g, b } = hexToRgb(hex);
    const bg = 255; // sfondo mappa approssimato a bianco
    const br = r * opacity + bg * (1 - opacity);
    const bgC = g * opacity + bg * (1 - opacity);
    const bb = b * opacity + bg * (1 - opacity);
    const luminance = 0.299 * br + 0.587 * bgC + 0.114 * bb;
    return luminance > 150 ? '#1a1a1a' : '#ffffff';
}

// Genera (con cache) lo stile del pallino per un dato conteggio, normalizzato
// sul massimo mostrato: colore sfumato, dimensione crescente e numero in contrasto.
const clusterStyleCache = new Map();
function getClusterStyle(count, maxCount) {
    const key = `${count}:${maxCount}`;
    const cached = clusterStyleCache.get(key);
    if (cached) return cached;

    const color = clusterColor(count, maxCount);
    const size = Math.round(46 + clusterNorm(count, maxCount) * 22);
    const stroke = 2;
    const radius = size / 2 - stroke;
    const c = size / 2;

    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
            <circle cx="${c}" cy="${c}" r="${radius}"
                fill="${color}" fill-opacity="${CLUSTER_FILL_OPACITY}"
                stroke="${color}" stroke-width="${stroke}" />
        </svg>
    `.trim();

    const style = {
        url: `data:image/svg+xml;base64,${btoa(svg)}`,
        width: size,
        height: size,
        textSize: 14,
        fontWeight: 'normal',
        // Numero sempre in contrasto col colore del pallino
        TEXT_COLOR: getContrastText(color, CLUSTER_FILL_OPACITY),
    };
    clusterStyleCache.set(key, style);
    if (clusterStyleCache.size > CLUSTER_STYLE_CACHE_LIMIT) {
        clusterStyleCache.delete(clusterStyleCache.keys().next().value);
    }
    return style;
}

const CLUSTER_RADIUS_PX = 80;

function getClusterCellSize(zoom) {
    const safeZoom = Math.max(0, zoom || 0);
    const degreesPerPixel = 360 / (256 * Math.pow(2, safeZoom));
    return CLUSTER_RADIUS_PX * degreesPerPixel;
}

function isPointInBounds(point, bounds) {
    if (!bounds) return true;
    const latitudeInBounds = point.lat >= bounds.south && point.lat <= bounds.north;
    const longitudeInBounds = bounds.west <= bounds.east
        ? point.lng >= bounds.west && point.lng <= bounds.east
        : point.lng >= bounds.west || point.lng <= bounds.east;
    return latitudeInBounds && longitudeInBounds;
}

function DeckHeatmapOverlay({ points, options, onMaxWeightChange }) {
    const map = useGoogleMap();
    const overlayRef = useRef(null);
    const [bounds, setBounds] = useState(null);

    const radiusPixels = useMemo(() => {
        // Match Google HeatmapLayer radius scaling with zoom
        const googleRadius = options?.radius || 20;
        return googleRadius * 1; // Half dimension
    }, [options?.radius]);

    useEffect(() => {
        if (!map) return undefined;
        const updateBounds = () => {
            const nextBounds = map.getBounds()?.toJSON();
            if (!nextBounds) return;
            setBounds(previous => {
                if (
                    previous &&
                    previous.north === nextBounds.north &&
                    previous.south === nextBounds.south &&
                    previous.east === nextBounds.east &&
                    previous.west === nextBounds.west
                ) {
                    return previous;
                }
                return nextBounds;
            });
        };
        updateBounds();
        const listener = map.addListener('idle', updateBounds);
        return () => listener.remove();
    }, [map]);

    const visiblePoints = useMemo(
        () => (points || []).filter(point => isPointInBounds(point, bounds)),
        [points, bounds]
    );

    const visibleScaleMax = useMemo(
        () => heatmapScaleMax(visiblePoints),
        [visiblePoints]
    );

    useEffect(() => {
        if (onMaxWeightChange) onMaxWeightChange(visiblePoints.length ? visibleScaleMax : 0);
    }, [visiblePoints.length, visibleScaleMax, onMaxWeightChange]);

    useEffect(() => {
        if (!map) return undefined;

        const overlay = new GoogleMapsOverlay({ layers: [] });
        overlay.setMap(map);
        overlayRef.current = overlay;

        return () => overlay.setMap(null);
    }, [map]);

    useEffect(() => {
        if (!overlayRef.current) return;

        if (!visiblePoints.length) {
            overlayRef.current.setProps({ layers: [] });
            return;
        }

        const layer = new DeckHeatmapLayer({
            id: "deck-heatmap-layer",
            data: visiblePoints,
            getPosition: d => [d.lng, d.lat],
            getWeight: d => heatmapWeight(d.weight, visibleScaleMax),
            radiusPixels,
            intensity: 1,
            threshold: 0.005,
            opacity: (options?.opacity || 0.6) * 0.7, // Added transparency
            colorDomain: [0, visibleScaleMax],
            colorRange: HEATMAP_COLOR_RANGE,
        });

        overlayRef.current.setProps({ layers: [layer] });
    }, [visiblePoints, visibleScaleMax, radiusPixels, options?.opacity]);

    return null;
}

export default function Heatmap({ filters, showOrganizationArea, showMarkers, overrideOrganization, isLayerVisible, cyclePaths, color, children, showPartials = true, }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const { id } = useParams();
    const isAll = id === "all";

    // Primary filters
    const baseKey = useMemo(() => JSON.stringify(filters), [filters]);
    const dateFilters = useMemo(() => ({
        ...filters,
        startDate: new Date(filters.minDate).getTime(),
        endDate: new Date(filters.maxDate).setDate(new Date(filters.maxDate).getDate() + 1),
    }), [baseKey]);
    // Filters for "all" endpoint
    const dateFiltersAll = useMemo(() => ({
        ...filters,
        startDate: new Date(filters.minDate).getTime(),
        endDate: new Date(filters.maxDate).getTime() + 24 * 60 * 60 * 1000,
    }), [baseKey]);

    // Data fetching
    const { polylines, status: polyStatus } = useGetOrganizationPolylines(
        id, dateFilters, !isAll, REFETCH_OPTIONS
    );
    const { polylines: polylinesAll, status: statusAll } = useGetOrganizationPolylinesAll(
        id, dateFiltersAll, isAll, REFETCH_OPTIONS
    );
    const { partials: markers, status: markStatus } = useGetOrganizationPartials(
        id, dateFilters, !isAll && showPartials, REFETCH_OPTIONS
    );
    const { partials: markersAll, status: statusMarkersAll } = useGetOrganizationPartialsAll(
        id, dateFiltersAll, isAll && showPartials, REFETCH_OPTIONS
    );

    // Choose data source based on isAll
    const effectivePolylines = isAll ? polylinesAll : polylines;
    const effectivePolyStatus = isAll ? statusAll : polyStatus;
    const effectiveMarkers = isAll ? markersAll : markers;
    const effectiveMarkStatus = isAll ? statusMarkersAll : markStatus;

    const { organization, status: orgStatus } = useGetOrganization(id, !overrideOrganization);
    // Define org centralized
    const org = useMemo(() => {
        if (overrideOrganization) return overrideOrganization;
        if (orgStatus === 'success') return organization;
        return null;
    }, [overrideOrganization, organization, orgStatus]);

    // Map state
    const [center, setCenter] = useState({ lat: 41.9028, lng: 12.4964 });
    const [zoom, setZoom] = useState(12);
    const [heatmapPoints, setHeatmapPoints] = useState([]);
    const [maxWeight, setMaxWeight] = useState(0);
    // Massimo dei cluster attualmente mostrati (per scala colore e legenda)
    const [clusterMaxCount, setClusterMaxCount] = useState(0);

    // Initialize center on org load
    useEffect(() => {
        if (org) {
            const orgGeo = org?.geojson ? JSON.parse(org.geojson) : null;
            const coords = orgGeo?.[0]?.[0] || org?.center || { lat: 41.9028, lng: 12.4964 };
            setCenter(coords);
        }
    }, [org]);

    // Process heatmap points
    useEffect(() => {
        if (
            effectivePolyStatus === "success" &&
            Array.isArray(effectivePolylines) &&
            effectivePolylines.length
        ) {
            const mapCount = new Map();
            let nCoordinates = 0;
            let midLat = 0;
            let midLng = 0;

            effectivePolylines.forEach(({ polyline }) => {
                polyline.forEach((seg) => {
                    decodePolyline(seg).forEach((pt, idx) => {
                        const key = `${pt.lat.toFixed(4)}:${pt.lng.toFixed(4)}`;
                        const count = mapCount.get(key) || 0;
                        const newCount = count + 1;
                        mapCount.set(key, newCount);

                        // Accumula per calcolo del centro solo i primi due punti di ogni segmento
                        if (idx === 0 || idx === 1) {
                            midLat += pt.lat;
                            midLng += pt.lng;
                            nCoordinates++;
                        }
                    });
                });
            });

            const points = Array.from(mapCount.entries()).map(([key, weight]) => {
                const [lat, lng] = key.split(":").map(Number);
                return {
                    lat,
                    lng,
                    weight,
                };
            });

            setHeatmapPoints(points);

            if (nCoordinates > 0) {
                setCenter({
                    lat: midLat / nCoordinates,
                    lng: midLng / nCoordinates,
                });
            }
        } else {
            setHeatmapPoints([]);
            setMaxWeight(0);
        }
    }, [effectivePolyStatus, JSON.stringify(effectivePolylines)]);

    // Manage markers
    // Marker filter settings
    const { startStop, pauseResume } = showMarkers;
    // Compute visible markers safely
    const visibleMarkers = useMemo(() => {
        if (effectiveMarkStatus !== 'success' || !Array.isArray(effectiveMarkers)) return [];

        let list = [];

        if (startStop) {
            list = list.concat(
                effectiveMarkers.filter(m =>
                    [partialType.START, partialType.END].includes(m.type)
                )
            );
        }

        if (pauseResume) {
            list = list.concat(
                effectiveMarkers.filter(m =>
                    [partialType.PAUSE, partialType.RESUME].includes(m.type)
                )
            );
        }

        // Filtra solo marker validi (lat/lng)
        list = list.filter(m =>
            typeof m.lat === 'number' && !isNaN(m.lat) &&
            typeof m.lng === 'number' && !isNaN(m.lng)
        );
        return list;
    }, [effectiveMarkStatus, effectiveMarkers, startStop, pauseResume]);

    const getMarkerIcon = useCallback((type) => {
        const iconUrl = type === partialType.START ? startIcon
            : type === partialType.END ? finishIcon
                : type === partialType.PAUSE ? pauseIcon
                    : resumeIcon;

        return {
            url: iconUrl,
            scaledSize: { width: 35, height: 48 },
        };
    }, []);

    // Heatmap and cluster options
    const heatmapOptions = useMemo(() => ({
        radius: zoom < 10 ? 1 :
            zoom < 12 ? 2 :
                zoom < 14 ? 5 :
                    zoom < 16 ? 10 :
                        zoom < 17 ? 17 :
                            zoom < 18 ? 18 :
                                20,
        opacity: 0.6,
        maxIntensity: 25,
        dissipating: true, // Usa sempre dissipating: true per zoom dinamico
    }), [zoom]);
    // Soglia 1: anche i parziali isolati sono un pallino "1", mai pin play/stop
    const minClusterSize = 1;

    const { markerClusters, singleMarkers } = useMemo(() => {
        if (!visibleMarkers.length) {
            return { markerClusters: [], singleMarkers: [] };
        }

        const cellSize = getClusterCellSize(zoom);
        const grid = new Map();

        visibleMarkers.forEach((marker, index) => {
            const cellX = Math.floor(marker.lng / cellSize);
            const cellY = Math.floor(marker.lat / cellSize);
            const key = `${cellX}:${cellY}`;
            let cell = grid.get(key);
            if (!cell) {
                cell = { key, sumLat: 0, sumLng: 0, markers: [] };
                grid.set(key, cell);
            }
            cell.sumLat += marker.lat;
            cell.sumLng += marker.lng;
            cell.markers.push({ ...marker, index });
        });

        const clusters = [];
        const singles = [];

        grid.forEach((cell) => {
            if (cell.markers.length >= minClusterSize) {
                clusters.push({
                    key: cell.key,
                    lat: cell.sumLat / cell.markers.length,
                    lng: cell.sumLng / cell.markers.length,
                    count: cell.markers.length,
                });
            } else {
                singles.push(...cell.markers);
            }
        });

        return {
            markerClusters: clusters,
            singleMarkers: singles,
        };
    }, [visibleMarkers, zoom]);

    useEffect(() => {
        if (!markerClusters.length) setClusterMaxCount(0);
    }, [markerClusters.length]);

    return (
        <Grid container className={classes.root}>
            {children}
            {(effectivePolyStatus === 'loading') && <CircularProgress style={{ margin: 'auto' }} />}
            {(effectivePolyStatus === 'success') && (
                <MapContainer center={center} zoom={zoom} onZoomChanged={setZoom} maxZoom={18} isLayerVisible={isLayerVisible} cyclePaths={cyclePaths} color={color}>
                    <DeckHeatmapOverlay
                        points={heatmapPoints}
                        options={heatmapOptions}
                        onMaxWeightChange={setMaxWeight}
                    />
                    {markerClusters.length > 0 && (
                        <MarkersClusterOverlay
                            clusters={markerClusters}
                            getStyle={getClusterStyle}
                            onMaxCountChange={setClusterMaxCount}
                        />
                    )}
                    {singleMarkers.map((marker) => (
                        <AdvancedMarker
                            key={`${marker.type}-${marker.index}-${marker.lat}-${marker.lng}`}
                            position={{ lat: marker.lat, lng: marker.lng }}
                            icon={getMarkerIcon(marker.type)}
                        />
                    ))}
                    {showOrganizationArea && <OrganizationAreaLayer organizationId={id} overrideOrganization={overrideOrganization} />}
                </MapContainer>
            )}
            <Grid item className={classes.legendStack}>
                {(startStop || pauseResume) && (
                    <div className={classes.legend}>
                        <div className={classes.legendTitle}>{t('overlappingPartials')}</div>
                        <div className={classes.clusterColorBar}></div>
                        <div className={classes.clusterScaleLabels}>
                            <span>1</span>
                            <span>{clusterMaxCount > 1 ? `>${gradientMax(clusterMaxCount)}` : ''}</span>
                        </div>
                    </div>
                )}
                <div className={classes.legend}>
                    <div className={classes.legendTitle}>{t('heatmap')}</div>
                    <div className={classes.colorBar}></div>
                    <div className={classes.clusterScaleLabels}>
                        <span>1</span>
                        <span>{`>${maxWeight || 1}`}</span>
                    </div>
                </div>
            </Grid>
        </Grid>
    );
}
